import React, { useState, useEffect } from 'react';
import { useUserWithDbId } from '@/modules/auth/hooks/useUserWithDbId';
import { api } from '../api';
import { CommentsModal } from './CommentsModal';
import { EditRequestForm } from './EditRequestForm';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { AuthPrompt } from '@/app/components/AuthPrompt';
import { ShareCopyModal } from '@/app/components/MarketingKitModal';
import { UserDisplayName } from '@/shared/components/UserDisplayName';
import { getFinalDisplayName } from '@/shared/utils/displayName';
import * as Sentry from '@sentry/browser';

export function RequestDetail({ request, onVoteChange, onRequestUpdated, onRequestDeleted }) {
  const { user } = useUserWithDbId();
  const [isVoting, setIsVoting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showShareCopy, setShowShareCopy] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localRequest, setLocalRequest] = useState(request);

  useEffect(() => {
    setLocalRequest(request);
    
    // Track request detail view
    if (request && request.id) {
      import('/src/services/analytics/index.js').then(({ trackViewRequestDetails }) => {
        trackViewRequestDetails(request.id);
      });
    }
  }, [request]);

  const isOwner = user && user.dbUserId && localRequest && localRequest.userId === user.dbUserId;

  const handleVote = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    
    if (isVoting) {
      console.log('Vote already in progress');
      return;
    }
    
    setIsVoting(true);
    
    // Store original state for rollback
    const originalVoted = localRequest.userHasVoted;
    const originalCount = localRequest.voteCount;
    
    try {
      console.log('Toggling vote for request:', localRequest.id);
      
      // Optimistic update
      const newVotedState = !originalVoted;
      const newCount = newVotedState ? originalCount + 1 : originalCount - 1;
      
      const updatedRequest = {
        ...localRequest,
        userHasVoted: newVotedState,
        voteCount: Math.max(0, newCount)
      };
      
      setLocalRequest(updatedRequest);
      
      // Notify parent component
      if (onVoteChange) {
        onVoteChange(localRequest.id, {
          userHasVoted: newVotedState,
          voteCount: Math.max(0, newCount)
        });
      }
      
      // Make API call
      await api.toggleCommitment(localRequest.id);
      
      // Track the vote action
      const { trackVoteRequest } = await import('/src/services/analytics/index.js');
      trackVoteRequest(localRequest.id, newVotedState ? 'up' : 'down');
      
    } catch (error) {
      console.error('Error toggling vote:', error);
      
      // Rollback optimistic update
      const rolledBackRequest = {
        ...localRequest,
        userHasVoted: originalVoted,
        voteCount: originalCount
      };
      
      setLocalRequest(rolledBackRequest);
      
      if (onVoteChange) {
        onVoteChange(localRequest.id, {
          userHasVoted: originalVoted,
          voteCount: originalCount
        });
      }
      
      Sentry.captureException(error, {
        extra: {
          requestId: localRequest.id,
          originalVoted,
          originalCount,
          userEmail: user?.email,
        }
      });
      
      // Show specific error message if user is trying to remove their own vote
      if (error.message && error.message.includes('Request creators cannot remove their interest')) {
        alert('You cannot remove your interest from your own request. Request creators are automatically interested in their requests.');
      } else {
        alert('Failed to update vote. Please try again.');
      }
    } finally {
      setIsVoting(false);
    }
  };

  const handleViewComments = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    setShowComments(true);
  };

  const handleCommentCountChange = (countDelta) => {
    setLocalRequest(prev => ({
      ...prev,
      commentCount: (prev.commentCount || 0) + countDelta
    }));
  };

  const handleGetShareCopy = () => {
    console.log('RequestDetail: Opening share copy modal for request:', localRequest.id);
    setShowShareCopy(true);
  };

  const handleCloseShareCopy = () => {
    console.log('RequestDetail: Closing share copy modal');
    setShowShareCopy(false);
  };

  const handleEditRequest = () => {
    console.log('RequestDetail: Opening edit form for request:', localRequest.id);
    setShowEditForm(true);
  };

  const handleSaveEdit = (updatedRequest) => {
    console.log('RequestDetail: Request updated:', updatedRequest);
    setLocalRequest(updatedRequest);
    setShowEditForm(false);
    
    // Notify parent component if callback provided
    if (onRequestUpdated) {
      onRequestUpdated(updatedRequest);
    }
  };

  const handleCancelEdit = () => {
    console.log('RequestDetail: Canceling edit');
    setShowEditForm(false);
  };

  const handleDeleteRequest = () => {
    console.log('RequestDetail: Opening delete confirmation for request:', localRequest.id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    
    try {
      console.log('RequestDetail: Deleting request:', localRequest.id);
      await api.deleteRequest(localRequest.id);
      
      console.log('RequestDetail: Request deleted successfully');
      setShowDeleteConfirm(false);
      
      // Notify parent component if callback provided
      if (onRequestDeleted) {
        onRequestDeleted(localRequest.id);
      }
    } catch (error) {
      console.error('RequestDetail: Error deleting request:', error);
      Sentry.captureException(error, {
        extra: {
          requestId: localRequest.id,
          userEmail: user?.email
        }
      });
      
      alert('Failed to delete request. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    console.log('RequestDetail: Canceling delete');
    setShowDeleteConfirm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInterestColor = () => {
    if (localRequest.voteCount === 0) return 'from-gray-400 to-gray-500';
    if (localRequest.voteCount < 10) return 'from-blue-400 to-blue-600';
    if (localRequest.voteCount < 50) return 'from-purple-400 to-purple-600';
    return 'from-green-400 to-green-600';
  };

  const wasUpdated = localRequest.updatedAt && new Date(localRequest.updatedAt) > new Date(localRequest.createdAt);

  // Get the display name to show
  const displayName = getFinalDisplayName(localRequest.userEmail, localRequest.userDisplayName);

  return (
    <>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Request Header */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                  {localRequest.title}
                </h1>
                <div className="ml-4 flex items-center gap-2 flex-shrink-0">
                  {/* Owner Badge */}
                  {isOwner && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Your Request
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <UserDisplayName 
                    displayName={displayName}
                    className="text-gray-900"
                  />
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Created: {formatDate(localRequest.createdAt)}
                </span>
                {wasUpdated && (
                  <span className="flex items-center text-purple-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Updated: {formatDate(localRequest.updatedAt)}
                  </span>
                )}
              </div>
            </div>
            
            {/* Vote Badge */}
            <div className="flex-shrink-0">
              <div className={`bg-gradient-to-r ${getInterestColor()} text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg text-center`}>
                {localRequest.voteCount} 👍
              </div>
              <p className="text-center text-sm text-gray-600 mt-2">
                {localRequest.voteCount === 0 ? 'No votes yet' :
                 localRequest.voteCount === 1 ? '1 vote' :
                 `${localRequest.voteCount} votes`}
              </p>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Full Description
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {localRequest.description}
            </p>
          </div>
        </div>

        {/* Owner Actions - Shown for request owners */}
        {isOwner && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Manage Your Request</h3>
                <p className="text-gray-600">
                  Update or delete your request
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleEditRequest}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold cursor-pointer transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Request
                </button>
                <button
                  onClick={handleDeleteRequest}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold cursor-pointer transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Request
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vote Action - Hidden for request owners */}
        {!isOwner && (
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Vote for This Request</h3>
                <p className="text-gray-600">
                  Help prioritize this request by voting for it
                </p>
              </div>
              
              <button
                onClick={handleVote}
                disabled={isVoting}
                className={`px-6 py-3 rounded-xl font-semibold cursor-pointer transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 flex items-center ${
                  user && localRequest.userHasVoted
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                } ${isVoting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isVoting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                    Processing...
                  </>
                ) : user && localRequest.userHasVoted ? (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Voted!
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.60L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                    </svg>
                    Vote for This
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Comments ({localRequest.commentCount || 0})
            </h2>
            <button
              onClick={handleViewComments}
              className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 cursor-pointer transition-colors flex items-center font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {user ? 'View & Add Comments' : 'View Comments'}
            </button>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4 text-center text-gray-600">
            <p>
              {user 
                ? 'Click "View & Add Comments" to participate in the discussion about this request.'
                : 'Sign in to participate in the discussion about this request.'
              }
            </p>
          </div>
        </div>

        {/* Enhanced Share Section - Moved to bottom */}
        <div className="bg-gradient-to-r from-violet-50 via-purple-50 to-blue-50 rounded-2xl border-2 border-violet-200 p-6 mb-6 shadow-lg">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367-2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Love This Idea? Help It Get Built!</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Get personalized posts that tell your story and encourage people to vote for this app. The more votes it gets, the higher priority it becomes!
                    <span className="font-semibold text-violet-700"> Your personal story could be what gets this app built!</span>
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>Personal stories get more votes = Higher priority = Gets built faster</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex-shrink-0">
              <button
                onClick={handleGetShareCopy}
                className="bg-gradient-to-r from-violet-600 to-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg cursor-pointer transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center gap-3"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367-2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                <span>Get People to Vote</span>
              </button>
            </div>
          </div>
          
          {/* Quick Share Stats */}
          <div className="mt-4 pt-4 border-t border-violet-200 flex flex-wrap items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-6">
              <span className="flex items-center text-gray-600">
                <svg className="w-4 h-4 mr-2 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {localRequest.voteCount} people interested
              </span>
              <span className="flex items-center text-gray-600">
                <svg className="w-4 h-4 mr-2 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {localRequest.commentCount || 0} discussing
              </span>
            </div>
            <div className="text-xs text-violet-600 font-medium">
              ✨ Share your story, get this app built!
            </div>
          </div>
        </div>

        {/* Floating Vote Campaign Reminder for Mobile */}
        <div className="md:hidden fixed bottom-6 right-4 z-40">
          <button
            onClick={handleGetShareCopy}
            className="bg-gradient-to-r from-violet-600 to-purple-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl cursor-pointer transition-all duration-200 hover:scale-110 active:scale-95"
            title="Get People to Vote"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367-2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuth && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 lg:p-8 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md sm:max-w-lg lg:max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Sign In Required</h2>
                <button
                  onClick={() => setShowAuth(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <AuthPrompt />
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showComments && (
        <CommentsModal
          request={localRequest}
          onClose={() => setShowComments(false)}
          onCommentCountChange={handleCommentCountChange}
        />
      )}

      {/* Share Copy Modal */}
      {showShareCopy && (
        <ShareCopyModal 
          request={localRequest}
          onClose={handleCloseShareCopy}
        />
      )}

             {/* Edit Request Form Modal */}
       {showEditForm && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
           <div className="w-full max-w-4xl max-h-screen overflow-y-auto">
             <EditRequestForm
               request={localRequest}
               onSave={handleSaveEdit}
               onCancel={handleCancelEdit}
             />
           </div>
         </div>
       )}

       {/* Delete Confirmation Modal */}
       {showDeleteConfirm && (
         <DeleteConfirmationModal
           isOpen={showDeleteConfirm}
           onClose={handleCancelDelete}
           onConfirm={handleConfirmDelete}
           requestTitle={localRequest.title}
           isDeleting={isDeleting}
         />
       )}
    </>
  );
}